/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing chat-store actions gate feature behavior from runtime flags. */
import { type Action, type StoreActionApi } from 'react-sweet-state';

import { type ExperienceNameType } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type ChatStorePrivateState,
	type StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { setConversationData } from './setConversationData';
import { storeErrorHandler } from './store-error-handler';
import type { ActionErrorHandler, ChatStoreContainerProps } from './types';

/**
 * The dispatchable action creator produced by {@link createSafeAction}.
 *
 * Given the same argument list as the wrapped action factory, it returns a
 * react-sweet-state `Action` whose result is `Promise<R>` (defaults to
 * `Promise<void>`).
 *
 * Exported so call sites can add the explicit type annotation required by
 * TypeScript's `--isolatedDeclarations`.
 */
export type SafeAction<TArgs extends unknown[], R = void> = (
	...args: TArgs
) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<R>>;

/**
 * Action Factory
 * Adds custom _middlewares_ to handle common cases and unexpected errors.
 * This is a hack to attach common logic to all actions.
 * It should not use the store action dispatcher. Instead, should call the actionCallback directly.
 *
 *
 * To add invocation-level error handling (`onError`):
 *
 * 1. Define actionargs type with `onError` using `OnErrorCallback`:
 *    ```ts
 *    type MyActionArgs = {
 *      myParam: string;
 *      onError?: OnErrorCallback<MyActionArgs>;
 *    };
 *    ```
 *
 * 2. Wire up `actionErrorHandler` to delegate to `onError`:
 *    ```ts
 *    createSafeAction(name, actionFn, {
 *      actionErrorHandler: (context) => {
 *        const { onError, ...args } = context.args?.[0] ?? {};
 *			if (onError) {
 *				return onError({ ...context, args });
 *			}
 *			// default action level error handling
 *			return { proceedToDefaultHandling: true };
 *      },
 *    });
 *    ```
 *
 */
export const createSafeAction =
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	<T extends (...args: any[]) => any, R = void>(
		actionLoggableName: StoreActionLoggableName,
		actionHOF: T,
		props?: {
			experienceName?: (args: Parameters<T>) => ExperienceNameType;
			getRateLimitState?: (args: Parameters<T>) => { isDeepResearchAttempt?: boolean };
			actionErrorHandler?: ActionErrorHandler<Parameters<T>>;
		},
	) =>
		(...args: Parameters<T>): Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<R>> =>
		async (api: StoreActionApi<ChatStorePrivateState>, containerProps: ChatStoreContainerProps) => {
			/*
		  Add custom logic here
		 */
			// General try/catch statement to handle unexpected errors
			try {
				addSentryBreadcrumb(`createSafeAction: executing ${actionLoggableName}`);
				// Here we call the action callback
				// Same as dispatch(actionHOF)
				const action = actionHOF(...args);
				// Here we execute the action i.e. what happens internally when the action is dispatched
				const result = await action(api, containerProps);
				return result as R;
			} catch (error: unknown) {
				storeErrorHandler({
					args,
					api,
					actionLoggableName,
					error,
					containerProps,
					setConversationData,
					experienceName: props?.experienceName,
					getRateLimitState: props?.getRateLimitState,
					actionErrorHandler: props?.actionErrorHandler,
				});
				return undefined as R;
			}
		};
